/**
 * «Помощь»: what the system does, in plain words (owner, 2026-10-29). Each
 * article states a rule the code enforces; a rule that changes is changed here
 * in the same release, so a client never reads one thing and sees another.
 */

export type Article = {
  readonly id: string;
  readonly section: Section;
  readonly title: string;
  readonly body: readonly string[];
};

export type Section = "Как работает бот" | "Деньги и комиссия" | "Защита счёта" | "Подключение";

export const SECTIONS: readonly Section[] = ["Как работает бот", "Деньги и комиссия", "Защита счёта", "Подключение"];

export const ARTICLES: readonly Article[] = [
  {
    id: "what",
    section: "Как работает бот",
    title: "Что делает Skryptos",
    body: [
      "Skryptos торгует на фьючерсах Bybit только в лонг: покупает монету и продаёт её дороже. Монеты бот выбирает сам по рейтингу Score — по движению цены, ликвидности и истории сделок всех счетов платформы.",
      "У каждой сделки есть сетка докупок: если цена идёт вниз, бот докупает частями и снижает среднюю цену входа. Когда цена возвращается выше средней на целевой процент, бот закрывает сделку с прибылью.",
      "Деньги всегда остаются на вашем счёте Bybit. Ключ, который вы даёте боту, разрешает только торговлю — выводить средства бот не может.",
    ],
  },
  {
    id: "pools",
    section: "Как работает бот",
    title: "Пулы PRIME, PULSE и FLASH",
    body: [
      "Сделки идут в двух пулах с разными правилами. PRIME — основной пул с более длинной сеткой. PULSE — быстрые сделки: небольшая сетка и цель около +3 %. В обоих пулах сделка открывается только по монете из двадцати лучших в рейтинге своего пула, которая раньше восстанавливалась почти после всех просадок. Пул FLASH выведен 4 ноября: новых сделок в нём нет, открытые доработают по своим правилам.",
      "Сколько сделок одновременно открыто, зависит от вашего капитала и от проверки риска (см. «Проверка на падение рынка на 50 %»).",
    ],
  },
  {
    id: "ladder",
    section: "Как работает бот",
    title: "Сетка докупок и средняя цена",
    body: [
      "После входа бот планирует несколько докупок ниже цены входа. Ордер докупки выставляется на биржу, когда цена подходит к нему ближе чем на 15 %: деньги не замораживаются заранее и работают в других сделках.",
      "В разделе «Сделки» видно, сколько ступеней уже куплено, где следующая докупка и сколько осталось до цели.",
    ],
  },
  {
    id: "trailing",
    section: "Как работает бот",
    title: "Трейлинг: как бот закрывает прибыльную сделку",
    body: [
      "Когда сделка выходит в плюс до цели, бот не продаёт сразу, а «ведёт» цену вверх. Если цена разворачивается вниз на небольшую величину, бот продаёт. Так прибыль может оказаться больше цели.",
      "Такие сделки отмечены меткой «трейлинг»; рядом написано, ниже какой цены бот не продаст. В «Сделках» есть фильтр «В трейлинге».",
    ],
  },
  {
    id: "red",
    section: "Как работает бот",
    title: "Почему сделка долго в минусе",
    body: [
      "Бот не закрывает сделки в минус по своему желанию: он ждёт, пока цена вернётся выше средней. Если рынок падает, докупки снижают среднюю цену, и для выхода в плюс нужен меньший отскок.",
      "Если вся сетка уже куплена, а цена так и не вернулась, через 14 дней бот ставит вторую такую же сетку от текущей цены: первый ордер сразу, следующие — когда цена до них дойдёт. Всего не больше маржи сделки, и деньги под ещё не достигнутые уровни остаются свободными.",
      "Исключение — делистинг монеты: тогда действуют особые правила (см. «Делистинг монеты»).",
    ],
  },
  {
    id: "no-deals",
    section: "Как работает бот",
    title: "Почему не открываются новые сделки",
    body: [
      "Бот открывает сделку, только когда находит подходящую монету и это безопасно для счёта. Причины паузы: проверка на падение рынка на 50 % не пропускает новый вход (счёт и так нагружен); подходящих монет сейчас нет — бот пропускает «тихие» монеты, монеты, выросшие больше чем на 150 % за неделю, и монеты у недельного максимума; закончился баланс комиссии; торговля остановлена в кабинете.",
      "Открытые сделки бот ведёт в любом случае — пауза касается только новых входов.",
    ],
  },
  {
    id: "fee",
    section: "Деньги и комиссия",
    title: "Комиссия платформы",
    body: [
      "Платформа берёт 20 % только с прибыли закрытых сделок. Нет прибыли — нет комиссии. Если после убытка счёт снова растёт, комиссия начисляется только когда прибыль превысит прежний максимум.",
      "Комиссия списывается с баланса комиссии, который пополняется переводом на Bybit по UID платформы (раздел «Пополнения и комиссия»).",
    ],
  },
  {
    id: "fee-balance",
    section: "Деньги и комиссия",
    title: "Баланс комиссии и лимит долга",
    body: [
      "Если на балансе не хватает денег, комиссия уходит в небольшой долг — до 100 USDT, а после пополнения на 10 USDT и больше — до 200 USDT; торговля продолжается. Когда долг доходит до лимита, новые сделки не открываются, пока вы не пополните баланс. Открытые сделки бот ведёт до закрытия.",
      "Когда до лимита остаётся мало, бот предупредит в Telegram.",
    ],
  },
  {
    id: "welcome",
    section: "Деньги и комиссия",
    title: "Приветственный бонус",
    body: [
      "Каждый новый клиент получает 20 USDT бонуса на баланс комиссии: первые сделки можно начать без пополнения. Бонус оплачивает только комиссию и не выводится.",
    ],
  },
  {
    id: "friends",
    section: "Деньги и комиссия",
    title: "Бонус за друзей и выплата в USDT",
    body: [
      "Пригласите друга по своей ссылке (раздел «Пополнения и комиссия» → «Пригласить друга»). Пока друг торгует, вы получаете 5 % от его прибыли — из комиссии платформы, ему это ничего не стоит.",
      "Бонус начисляется с той комиссии, которую друг оплатил своими деньгами, и сначала идёт на оплату вашей комиссии. Когда реферального бонуса накопится 200 USDT и больше, его можно запросить в USDT — платформа переведёт на ваш Bybit UID.",
    ],
  },
  {
    id: "money-where",
    section: "Деньги и комиссия",
    title: "Где мои деньги",
    body: [
      "Все деньги — на вашем счёте Bybit. Платформа не хранит и не выводит ваши средства. Вывести деньги можете только вы, в приложении Bybit. Перед выводом остановите торговлю и дождитесь закрытия открытых сделок.",
    ],
  },
  {
    id: "stress",
    section: "Защита счёта",
    title: "Проверка на падение рынка на 50 %",
    body: [
      "Перед каждой новой сделкой бот считает, что будет, если все ваши монеты одновременно упадут на 50 % и докупятся все запланированные ступени. Если после такого падения у счёта останется меньше 15 % капитала сверх маржи поддержания Bybit, новая сделка не открывается.",
      "Поэтому число сделок зависит от вашего капитала: бот не берёт больше риска, чем счёт выдержит в сильный обвал.",
    ],
  },
  {
    id: "delisting",
    section: "Защита счёта",
    title: "Делистинг монеты",
    body: [
      "Когда Bybit объявляет, что снимет монету с торгов, бот сразу отменяет докупки и придёт уведомление. Дальше: если позиция в минусе больше 10 %, бот закрывает её сразу — у таких монет до делистинга цена чаще падает дальше. Иначе бот закрывает её при первой небольшой прибыли (+0,2 %), а за 2 часа до делистинга — по рынку в любом случае.",
      "Убыток из-за делистинга платформа возвращает вам бонусом на баланс комиссии.",
    ],
  },
  {
    id: "crash",
    section: "Защита счёта",
    title: "Что будет при обвале рынка",
    body: [
      "Бот не продаёт в минус из-за падения: он докупает по сетке и ждёт отскока. Новые сделки при этом не открываются, если не проходит проверка на падение рынка на 50 %. Это не исключает убыток: в сильный и долгий обвал позиции могут долго оставаться в минусе. Торгуйте только теми деньгами, риск потери которых для вас допустим.",
    ],
  },
  {
    id: "bybit-key",
    section: "Подключение",
    title: "Как создать ключ Bybit",
    body: [
      "На bybit.com откройте профиль → API → «Создать новый ключ» → «Ключи API, созданные системой». Назначение — «Транзакция по API».",
      "Разрешения: «Чтение и запись»; в «Едином торговом аккаунте» отметьте ордера и позиции по контрактам. Вывод средств не включайте. Скопируйте API Key и Secret и вставьте их в разделе «Настройки» → Bybit.",
    ],
  },
  {
    id: "telegram",
    section: "Подключение",
    title: "Уведомления в Telegram",
    body: [
      "В «Настройках» нажмите «Подключить Telegram» — откроется бот @Skryptos_bot, нажмите «Start». Бот присылает открытия и закрытия сделок, прибыль за день, предупреждения о балансе и делистинге. Команды /status и /profit показывают состояние счёта.",
    ],
  },
  {
    id: "stop",
    section: "Подключение",
    title: "Как остановить бота",
    body: [
      "На главной странице кабинета нажмите «Остановить». Новые сделки перестанут открываться, а открытые бот доведёт до закрытия. Остановка не закрывает сделки и не трогает деньги на бирже.",
    ],
  },
  {
    id: "share",
    section: "Подключение",
    title: "Как поделиться результатом",
    body: [
      "В «Доходности» на главной и у каждой закрытой сделки есть кнопка «Поделиться»: она делает картинку с результатом. Сумма по умолчанию скрыта — включите «показать сумму», если хотите её показать.",
    ],
  },
];

/** Articles whose title or text holds every word of the query. */
export function search(query: string): readonly Article[] {
  const words = query.toLowerCase().split(/\s+/u).filter((word) => word !== "");
  if (words.length === 0) return ARTICLES;
  return ARTICLES.filter((article) => {
    const text = `${article.title} ${article.body.join(" ")}`.toLowerCase();
    return words.every((word) => text.includes(word));
  });
}
